import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import ProgressBar from "react-bootstrap/ProgressBar";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import iconTVG from "../../../../Resources/Images/icono_TVG.png";
import style from "./style.module.css";
import styleHover from "../PlaylistItem/style.module.css";
import { URL_LOGIN, URL_VIDEODETAIL, URL_DETAIL } from "../../../Routes/Location";
import { useIsLogin } from "../../utils/utils";
import {actionTypeContainer, getPreviewUrl, getAgeRestrictionLabel, generateSlug} from "../../../../Utils/utils";
import ActionButtons from "../ActionButtons/ActionButtons";
import VideoType from "../../../../../../detail/src/Pages/DetailPage/VideoType";
import EditorialTag from "../../../common/EditorialTag/EditorialTag";
import {
  WIDGET_ACTION_PLAY,
  WIDGET_TYPE_LIVE_V2,
  WIDGET_TYPE_POSTER,
  WIDGET_TYPE_PROGRESS,
  AGE_RESTRICTION_7_PLUS,
  AGE_RESTRICTION_12_PLUS,
  AGE_RESTRICTION_16_PLUS,
  AGE_RESTRICTION_18_PLUS
} from "../../../../Utils/Constants";
import { useTranslation } from "react-i18next";


const MediaCover = ({ data, typeBanner, event, trigger }) => {

  const { id, title, video, tags=[], progress, editorial_tags, rating } = data;
  const {t} = useTranslation();
  const isLogin = useIsLogin();
  const linkRef = useRef(null);
  const isTVG = tags.includes("TVG");
  const [previewUrl, setPreviewUrl] = useState(null);
  const timeoutRef = useRef(null);
  const [isHoveredTime, setIsHoveredTime] = useState(false);
  const dragStateRef = useRef({
    isPointerDown: false,
    moved: false,
    startX: 0,
    startY: 0,
  });

  useEffect(() => {
    const handlePointerMove = (event) => {
      const state = dragStateRef.current;
      if (!state.isPointerDown) {
        return;
      }
      const deltaX = Math.abs(event.clientX - state.startX);
      const deltaY = Math.abs(event.clientY - state.startY);
      if (Math.max(deltaX, deltaY) > 5) {
        state.moved = true;
      }
    };

    const handlePointerUp = () => {
      const state = dragStateRef.current;
      state.isPointerDown = false;
      state.startX = 0;
      state.startY = 0;
      requestAnimationFrame(() => {
        state.moved = false;
      });
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, []);

  const showPreviews = useSelector((state) => state.commonState.previews);

  const tenantIsFreeToPlay = useSelector((state) => state.commonState.tenantStyles.isFreeToPlay);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const goToLink = () => {
    if (linkRef.current) {
      linkRef.current.click();
    }
  };

  const isOnAir = () => {
    if (Number.isNaN(data.start)) return false;
    const timestamp = data.start * 1000; // Convert to milliseconds
    const dateFromTimestamp = new Date(timestamp);
    const currentDate = new Date();
    const isTimestampEarlier = dateFromTimestamp < currentDate;
    return isTimestampEarlier;
  };

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(async () => {
      if (!previewUrl) {
        const url = await getPreviewUrl(id);
        setPreviewUrl(url);
      }
      setIsHoveredTime(true);
    }, 1000);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsHoveredTime(false);
  };

  const getImage = () => {
    const banner = typeBanner === WIDGET_TYPE_POSTER ? (data.poster || data.banner) : (data.banner || data.poster);

    return (
        <Link
          ref={linkRef}
          to={getLinkProps()}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onPointerDown={(event) => {
            dragStateRef.current.isPointerDown = true;
            dragStateRef.current.moved = false;
            dragStateRef.current.startX = event.clientX;
            dragStateRef.current.startY = event.clientY;
          }}
          onClick={(event) => {
            if (dragStateRef.current.moved) {
              event.preventDefault();
              event.stopPropagation();
              dragStateRef.current.moved = false;
            }
          }}
          onDragStart={(e) => e.preventDefault()}
        >
          {isHoveredTime && showPreviews && !!previewUrl ? (
              <video
                  className={`${style.playlistItemImage} ${
                      typeBanner === WIDGET_TYPE_POSTER ? style.playlistItemImage__vertical : ""
                  }`}
                  alt={t('global.image') + title}
                  muted
                  autoPlay
                  loop
                  onError={(e) => {
                    e.target.pause();
                    e.target.src = "";
                  }}
              >
              <source src={previewUrl} type="video/webm" />
              <source src={previewUrl} type="video/mp4" />
                Your browser does not support the video tag.
            </video>
          ) : (
          <img
              className={`${style.playlistItemImage} ${styleHover.playlistItemImage} ${
            typeBanner === WIDGET_TYPE_POSTER ? style.playlistItemImage__vertical : ""
          }`}
          src={banner}
          alt={t('global.image') + title}
        />)}
        {typeBanner === WIDGET_TYPE_PROGRESS && <ProgressBar now={progress} className={style.barProgress} aria-label={t('global.progressbar') + title} />}
   </Link>
    );
  };

  const getLinkProps = () => {
    const route = actionTypeContainer(trigger)?.ROUTE;
    const slug = (route === URL_VIDEODETAIL || route === URL_DETAIL) && title ? generateSlug(title) : null;
    const accessResult = route + "/" + id + (slug ? "/" + slug : "");
    const pathname = !!tenantIsFreeToPlay && !isLogin && accessResult === WIDGET_ACTION_PLAY ? URL_LOGIN : accessResult;

    if (!video) return { pathname: pathname, state: { id } };

    const url = video?.url;
    const type = video?.type;

    return {
      pathname: pathname,
      state: { url, type, id },
    };
  };

  const AGE_BADGE_CLASSES = {
    [AGE_RESTRICTION_7_PLUS]: style["ageBadge--7"],
    [AGE_RESTRICTION_12_PLUS]: style["ageBadge--12"],
    [AGE_RESTRICTION_16_PLUS]: style["ageBadge--16"],
    [AGE_RESTRICTION_18_PLUS]: style["ageBadge--18"],
  };

  const ageBadgeLabel = getAgeRestrictionLabel(rating?.code);
  const ageBadgeClass = AGE_BADGE_CLASSES[rating?.code];

  return (
    <div className={`${style.playlistHeader} ${styleHover.playlistHeader}`}>
      {getImage()}
      {isTVG && <img className={style.iconTVG} src={iconTVG} alt={"iconTVG"} />}
      {ageBadgeLabel && (
        <span className={`${style.ageBadge} ${ageBadgeClass || ""}`}>
          {ageBadgeLabel}
        </span>
      )}
      {actionTypeContainer(trigger)?.SHOW_BUTTONS && (
        <ActionButtons data={data} trigger={trigger} event={event} actionLink={false} goToLink={goToLink} />
      )}
      {typeBanner === WIDGET_TYPE_LIVE_V2 && <VideoType type={data.type} subtitle={data} isLiveWidget onAir={isOnAir()} editorialTags={editorial_tags} />}
      {typeBanner !== WIDGET_TYPE_LIVE_V2 && !!editorial_tags && editorial_tags.length > 0 &&(
        <EditorialTag
          editorialTags={editorial_tags}
          className={typeBanner === WIDGET_TYPE_POSTER ? "poster" : ""}
        />
      )}
    </div>
  );
};
export default MediaCover;
MediaCover.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    tags: PropTypes.array,
  }).isRequired,
  typeBanner: PropTypes.string,
  trigger: PropTypes.string.isRequired,
};
